import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ImageDown, Save, RotateCcw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { PageHeader, Panel, BrandButton } from "@/components/PageHeader";
import { DelegateSelect } from "@/components/DelegateSelect";
import { StickerPage, FIELD_LABELS, FONTS, DPIS, loadTemplate, stickerData, pageSize, downloadStickerImage } from "@/components/labels/Sticker";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";

const SAMPLE = { name: "Ama Serwaa Mensah", ticket_no: "GAMLS26-0001", pin: "MLS00000", gamls_id: "A12345678901", category: "Member in Good Standing", region: "Greater Accra",
  facility: "Korle Bu Teaching Hospital", phone: "0240000000", email: "participant@example.org", event: "Annual Scientific Conference" };
const PX_PER_MM = 3.78;

const Num = ({ label, value, onChange, step = 1, testId, suffix }) => (
  <label className="block"><span className="text-xs font-semibold text-gray-600">{label}{suffix && <span className="text-gray-400 font-normal"> ({suffix})</span>}</span>
    <Input type="number" step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} className="h-9 mt-1" data-testid={testId} /></label>
);

const Choice = ({ label, value, options, onChange, testId, className = "" }) => (
  <label className={`block ${className}`}><span className="text-xs font-semibold text-gray-600">{label}</span>
    <div className="flex gap-1 mt-1">{options.map(([v, l]) => (
      <button key={v} type="button" onClick={() => onChange(v)} className={`flex-1 h-9 rounded-md border text-sm transition-colors ${value === v ? "border-[#E60000] bg-[#FFF0F0] text-[#B30000] font-semibold" : "border-[#E5E7EB] hover:border-gray-400"}`} data-testid={`${testId}-${v}`}>{l}</button>
    ))}</div></label>
);

const Sizes = ({ t, set }) => (
  <div className="grid grid-cols-2 gap-3">
    <Num label="Design width" suffix="mm" value={t.width_mm} onChange={(v) => set({ width_mm: v })} testId="label-width" />
    <Num label="Design height" suffix="mm" value={t.height_mm} onChange={(v) => set({ height_mm: v })} testId="label-height" />
    <Num label="Margin" suffix="mm" step={0.5} value={t.padding_mm} onChange={(v) => set({ padding_mm: v })} testId="label-padding" />
    <Num label="QR size" suffix="mm" value={t.qr_size_mm} onChange={(v) => set({ qr_size_mm: v })} testId="label-qr-size" />
    <Num label="Name size" suffix="pt" value={t.name_size_pt} onChange={(v) => set({ name_size_pt: v })} testId="label-name-size" />
    <Num label="Text size" suffix="pt" value={t.text_size_pt} onChange={(v) => set({ text_size_pt: v })} testId="label-text-size" />
    <Choice label="QR position" value={t.qr_side} options={[["left", "Left"], ["right", "Right"]]} onChange={(v) => set({ qr_side: v })} testId="label-qr" />
    <Choice label="Text alignment" value={t.align || "left"} options={[["left", "Left"], ["center", "Centre"]]} onChange={(v) => set({ align: v })} testId="label-align" />
    <Choice className="col-span-2" label="Picture orientation" value={t.orientation || "landscape"} options={[["landscape", "Landscape"], ["portrait", "Portrait (turned sideways)"]]} onChange={(v) => set({ orientation: v })} testId="label-orientation" />
    <label className="block"><span className="text-xs font-semibold text-gray-600">Font</span>
      <select value={t.font || "Arial"} onChange={(e) => set({ font: e.target.value })} className="mt-1 w-full h-9 rounded-md border border-[#E5E7EB] bg-white px-2 text-sm" data-testid="label-font">
        {FONTS.map((f) => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
      </select></label>
    <label className="block"><span className="text-xs font-semibold text-gray-600">Picture quality</span>
      <select value={t.dpi || 300} onChange={(e) => set({ dpi: Number(e.target.value) })} className="mt-1 w-full h-9 rounded-md border border-[#E5E7EB] bg-white px-2 text-sm" data-testid="label-dpi">
        {DPIS.map((d) => <option key={d} value={d}>{d} dpi{d === 203 ? " (most Gainscha printers)" : d === 300 ? " (sharp)" : " (extra sharp)"}</option>)}
      </select></label>
    <label className="col-span-2 flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={!!t.border} onCheckedChange={(v) => set({ border: !!v })} data-testid="label-border" />Draw a thin border around the sticker</label>
    {t.qr_size_mm > t.height_mm - 2 * t.padding_mm && <p className="col-span-2 text-xs text-amber-700" data-testid="label-qr-fit-warning">The QR code is bigger than the sticker height, so it is drawn at {Math.max(5, t.height_mm - 2 * t.padding_mm)} mm.</p>}
  </div>
);

const Fields = ({ t, set }) => (
  <div className="space-y-3">
    <div className="grid grid-cols-2 gap-2">
      {Object.entries(FIELD_LABELS).map(([k, l]) => (
        <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox checked={!!t.fields[k]} onCheckedChange={(v) => set({ fields: { ...t.fields, [k]: !!v } })} data-testid={`label-field-${k}`} />{l}
        </label>
      ))}
    </div>
    {t.fields.header && <label className="block"><span className="text-xs font-semibold text-gray-600">Header text</span><Input value={t.header_text} maxLength={60} onChange={(e) => set({ header_text: e.target.value })} className="h-9 mt-1" data-testid="label-header-text" /></label>}
    {t.fields.footer && <label className="block"><span className="text-xs font-semibold text-gray-600">Footer text</span><Input value={t.footer_text || ""} maxLength={60} onChange={(e) => set({ footer_text: e.target.value })} placeholder="e.g. Access: All sessions" className="h-9 mt-1" data-testid="label-footer-text" /></label>}
  </div>
);

const Preview = ({ t, d }) => {
  const [pw, ph] = pageSize(t);
  const scale = Math.min(1.6, 520 / (pw * PX_PER_MM), 420 / (ph * PX_PER_MM));
  const dpi = t.dpi || 300;
  return (
    <div className="rounded-lg bg-[#F4F5F7] border border-dashed border-gray-300 min-h-[360px] grid place-items-center p-6 overflow-auto" data-testid="label-preview">
      <div className="shadow-xl ring-1 ring-gray-300 bg-white overflow-hidden" style={{ width: pw * PX_PER_MM * scale, height: ph * PX_PER_MM * scale }} data-testid="label-preview-page">
        <div style={{ transform: `scale(${scale})`, transformOrigin: "top left", width: "fit-content" }}><StickerPage t={t} d={d} /></div>
      </div>
      <p className="text-xs text-gray-500 mt-4 text-center" data-testid="label-preview-size">Picture {pw} × {ph} mm · {Math.round((pw / 25.4) * dpi)} × {Math.round((ph / 25.4) * dpi)} px at {dpi} dpi · shown at {Math.round(scale * 100)}%</p>
    </div>
  );
};

export default function LabelStudio() {
  const { delegates } = useApp();
  const [t, setT] = useState(null);
  const [saved, setSaved] = useState(null);
  const [id, setId] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { loadTemplate().then((v) => { setT(v); setSaved(v); }); }, []);
  const set = (p) => setT((s) => ({ ...s, ...p }));
  const d = delegates.find((x) => x.id === id);
  const sample = d ? stickerData({ ...d, delegate_name: d.name }, SAMPLE.event) : SAMPLE;
  const dirty = JSON.stringify(t) !== JSON.stringify(saved);
  const save = async () => {
    setBusy(true);
    try { const r = await api.put("/label-template", t); setT(r.data); setSaved(r.data); toast.success("Sticker template saved. Every downloaded picture now uses it."); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const download = async () => {
    try { const r = await downloadStickerImage(t, sample); toast.success(`Downloaded ${r.name} (${r.width} × ${r.height} px)`); }
    catch (e) { toast.error(`Could not create the picture: ${e?.message || e}`); }
  };
  if (!t) return <div className="text-gray-500">Loading…</div>;
  return (
    <div data-testid="label-studio-page">
      <PageHeader eyebrow="Control" title="Gainscha Label Studio" description="Design the sticker picture. Download it for each participant from the registration details window in the Event Registrations List, then import it into BarTender and print." />
      <div className="grid grid-cols-1 xl:grid-cols-[400px_1fr] gap-4 items-start">
        <div className="space-y-4">
          <Panel title="Size and layout"><Sizes t={t} set={set} /></Panel>
          <Panel title="Show on sticker"><Fields t={t} set={set} /></Panel>
          <div className="flex gap-2">
            <BrandButton className="flex-1" onClick={save} disabled={busy || !dirty} data-testid="label-save"><Save className="w-4 h-4" />{dirty ? "Save template" : "Saved"}</BrandButton>
            <BrandButton variant="outline" onClick={() => setT(saved)} disabled={!dirty} data-testid="label-revert"><RotateCcw className="w-4 h-4" />Undo</BrandButton>
          </div>
        </div>
        <div className="space-y-4">
          <Preview t={t} d={sample} />
          <Panel title="Try it">
            <div className="flex flex-col md:flex-row gap-3 md:items-end">
              <div className="flex-1"><div className="text-xs font-semibold text-gray-600 mb-1">Preview with a participant (optional)</div><DelegateSelect value={id} onChange={setId} testId="label-delegate-select" /></div>
              <BrandButton variant="dark" onClick={download} data-testid="label-download-sample"><ImageDown className="w-4 h-4" />Download sample picture</BrandButton>
            </div>
            <p className="text-xs text-gray-500 mt-3" data-testid="label-bartender-help">The picture is a PNG at the exact sticker size, so BarTender places it at {pageSize(t).join(" × ")} mm. In BarTender, set the label to that size, insert the picture, stretch it to fill the label, and print.</p>
          </Panel>
        </div>
      </div>
    </div>
  );
}
